import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

export default function Content({ searchParams }) {
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [checkedItems, setCheckedItems] = useState(new Set());
  const [checkAll, setCheckAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // 1: 고객센터문의 , 0:제휴문의
  const [selectedCategory, setSelectedCategory] = useState(1);

  // 카테고리 변경 핸들러
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // 카테고리 변경 시 첫 페이지로
    setCheckedItems(new Set()); // 체크박스 초기화
    setCheckAll(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (searchParams?.keyword && currentPage !== 1) {
          setCurrentPage(1);
          return;
        }

        console.log('API 호출:', {
          category: selectedCategory,
          page: currentPage,
          keyword: searchParams?.keyword || '',
          searchType: searchParams?.searchType || '',
        });

        const apiParams = {
          curPage: currentPage,
          // category는 URL 파라미터로 전달되므로 제거
        };

        if (searchParams?.keyword && searchParams.keyword.trim()) {
          apiParams.keyword = searchParams.keyword.trim();
          apiParams.filter = searchParams.searchType || 'title';
        }

        console.log('전송할 파라미터:', apiParams);

        // 기존 API 엔드포인트 유지
        const response = await axios.get(
          `/api/admin/contact/cateId/${selectedCategory}`,
          {
            params: apiParams,
            timeout: 10000,
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('API 응답:', response.data);

        if (response.data && response.data.success !== false) {
          const contactList = response.data.list || [];
          const paginationData = response.data.pagination || {};

          setContacts(contactList);
          setPagination(paginationData);

          // 관리자 페이지용 페이지네이션 정보 처리
          if (paginationData.totalCount) {
            setTotalCount(paginationData.totalCount);
          }

          // 페이지네이션 계산 로직 수정
          const totalPages = paginationData.totalPages || 1;
          const pageSize = paginationData.pageSize || 10;
          const currentPageForCalc = paginationData.currentPage || currentPage;

          // 페이지 그룹 계산 (10페이지씩 그룹화)
          const pageGroup = Math.floor((currentPageForCalc - 1) / 10);
          const startPage = pageGroup * 10 + 1;
          const endPage = Math.min(startPage + 9, totalPages);

          // 목록 번호 계산 (역순)
          const startNo =
            paginationData.totalCount - (currentPageForCalc - 1) * pageSize;

          const adminPagination = {
            pageCnt: totalPages,
            startPage: startPage,
            endPage: endPage,
            startNo: startNo,
            listCnt: paginationData.totalCount || 0,
            currentPage: currentPageForCalc,
            pageSize: pageSize,
          };

          console.log('계산된 페이지네이션:', adminPagination);
          setPagination(adminPagination);
        } else {
          console.error('API 오류:', response.data.error);
          setError(response.data.error || 'API 호출에 실패');
        }
      } catch (error) {
        console.error('데이터 로드 오류:', error);

        let errorMessage = '데이터를 불러오는 중 오류 발생';

        if (error.response) {
          errorMessage = `서버 오류 (${error.response.status}): ${
            error.response.data.message || error.response.statusText
          }`;
        } else if (error.request) {
          errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
        } else {
          errorMessage = error.message;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, selectedCategory, searchParams]);

  const handlePageChange = (pageNum) => {
    console.log('페이지 변경:', pageNum);
    setCurrentPage(pageNum);
  };

  const renderPagination = () => {
    if (!pagination || !pagination.pageCnt) {
      return null;
    }

    const pages = [];
    const pageCnt = pagination.pageCnt || 0;
    const startPage = pagination.startPage || 1;
    const endPage = pagination.endPage || 1;

    // 처음으로
    pages.push(
      <li key="first" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage > 1) handlePageChange(1);
          }}
        >
          《
        </a>
      </li>
    );

    // 이전
    pages.push(
      <li key="prev" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage > 1) handlePageChange(currentPage - 1);
          }}
        >
          〈
        </a>
      </li>
    );

    // 페이지 번호들
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={i === currentPage ? 'num_on' : 'num'}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }

    // 다음
    pages.push(
      <li key="next" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage < pageCnt) handlePageChange(currentPage + 1);
          }}
        >
          〉
        </a>
      </li>
    );

    // 마지막으로
    pages.push(
      <li key="last" className="num">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage < pageCnt) handlePageChange(pageCnt);
          }}
        >
          》
        </a>
      </li>
    );

    return pages;
  };

  const handleDelete = async () => {
    if (checkedItems.size === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (window.confirm('선택한 항목을 삭제하시겠습니까?')) {
      try {
        const deleteIds = Array.from(checkedItems);

        const response = await axios.post('/api/admin/contact/delete', {
          ids: deleteIds,
        });

        if (response.data.success) {
          alert(response.data.message || '선택한 항목이 삭제되었습니다.');
          setCheckedItems(new Set());
          setCheckAll(false);
          // 현재 페이지 데이터 새로고침
          window.location.reload();
        } else {
          alert(response.data.message || '삭제 처리 중 오류가 발생했습니다.');
        }
      } catch (error) {
        console.error('삭제 오류:', error);
        alert(
          '삭제 중 오류가 발생했습니다: ' +
            (error.response?.data?.message || error.message)
        );
      }
    }
  };

  const handleCheckAll = (e) => {
    const isChecked = e.target.checked;
    setCheckAll(isChecked);

    if (isChecked) {
      const allIds = new Set(contacts.map((contact) => contact.c_no));
      setCheckedItems(allIds);
    } else {
      setCheckedItems(new Set());
    }
  };

  const handleCheckItem = (contactId) => {
    const newCheckedItems = new Set(checkedItems);

    if (newCheckedItems.has(contactId)) {
      newCheckedItems.delete(contactId);
    } else {
      newCheckedItems.add(contactId);
    }

    setCheckedItems(newCheckedItems);
    setCheckAll(
      newCheckedItems.size === contacts.length && contacts.length > 0
    );
  };

  if (loading) {
    return (
      <div className="txt-center" style={{ marginTop: '60px' }}>
        로딩 중...
      </div>
    );
  }

  return (
    <>
      {/* 카테고리 필터 라디오 버튼 
      <div className="row-flex-center gap-20">
        <div className="row-flex-center gap-10 mt-2">
          <input
            type="radio"
            value={1}
            id="category2"
            name="contactCategory"
            checked={selectedCategory === 1}
            onChange={() => handleCategoryChange(1)}
          />
          <label htmlFor="category2" className="prt-regular fs-16 pointer">
         고객센터(문의)
          </label>
        </div>

        <div className="row-flex-center gap-10 mt-2">
          <input
            type="radio"
            value={0}
            id="category1"
            name="contactCategory"
            checked={selectedCategory === 0}
            onChange={() => handleCategoryChange(0)}
          />
          <label htmlFor="category1" className="prt-regular fs-16 pointer">
          제휴문의
          </label>
        </div>
      </div>
*/}

      {/* 에러 메시지 */}
      {error && (
        <div
          className="error-message"
          style={{ color: 'red', margin: '20px 0' }}
        >
          {error}
        </div>
      )}

      {/* 데이터 테이블 */}
      <div className="over_hi" style={{ marginTop: '60px' }}>
        <form id="deleteFrm" method="post">
          <table className="adminTable">
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '50%' }} />
              {/* <col style={{ width: '10%' }} /> */}
              <col style={{ width: '10%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    id="checkall"
                    checked={checkAll}
                    onChange={handleCheckAll}
                  />
                </th>
                <th>No</th>
                <th>카테고리</th>
                {/* <th>제목</th> */}
                <th>작성자</th>
                <th>작성날짜</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="txt-center">
                    {selectedCategory === 1 ? '고객센터(문의)가' : '제휴문의가'}{' '}
                    없습니다.
                  </td>
                </tr>
              ) : (
                contacts.map((contact, index) => {
                  // 스프링 레거시 DB 컬럼명에 맞춰 수정
                  const contactId = contact.c_no;
                  const contactTitle = contact.c_title || '';
                  const contactCategory = contact.c_category1;
                  const contactDate = contact.c_add_dt;
                  const contactName = contact.c_name || '';
                  const companyName = contact.c_company_name || '';

                  return (
                     <tr className='pointer'
  key={contactId} 
  onClick={() => window.location.href = `/admin/contact_d/${contactId}`}
>
                      <td>
                        <input
                          type="checkbox"
                          value={contactId}
                          name="check"
                          checked={checkedItems.has(contactId)}
                          onChange={() => handleCheckItem(contactId)}
                        />
                      </td>
                      <td>
                        {pagination.startNo && pagination.startNo > 0
                          ? pagination.startNo - index
                          : (totalCount || 0) - (currentPage - 1) * 10 - index}
                      </td>
                      <td className="txt-center">
                        <span
                          className={`status-badge ${
                            contactCategory === 1
                              ? 'status-active'
                              : 'status-ended'
                          }`}
                        >
                          {contactCategory === 1
                            ? '고객센터(문의)'
                            : '제휴문의'}
                        </span>
                      </td>
                      {/* <td className="left">
                        <a href={`/admin/contact_d/${contactId}`}>
                          {contactTitle}
                        </a>
                      </td> */}
                      <td className="txt-center">
                        {companyName
                          ? `${contactName}(${companyName})`
                          : contactName}
                      </td>
                      <td className="txt-center">
                        {contactDate
                          ? format(new Date(contactDate), 'yyyy/MM/dd HH:mm')
                          : ''}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </form>
      </div>

      {/* 페이지네이션 */}
      <ul className="txt-center pageing">{renderPagination()}</ul>

      {/* 액션 버튼들 */}
      <div className="row-flex flex-end gap-10">
        <input
          type="button"
          value="삭제"
          className="common-del"
          onClick={handleDelete}
        />
      </div>
    </>
  );
}
